/**
 * MediaBlockOverlay — renders download/delete action buttons over media blocks
 * (images, videos, files) on hover. Also adds a "click below to continue" affordance.
 *
 * Attaches to the editor container via MutationObserver to detect media blocks
 * and overlay action buttons on them.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { BlockNoteEditor } from '@blocknote/core';
import * as pagesApi from '../../api/pages-api';
import { DownloadConfirmModal, DownloadTarget } from '../../components/DownloadConfirmModal';

interface MediaBlockOverlayProps {
  readonly editor: BlockNoteEditor<any, any, any> | null;
  readonly containerRef: React.RefObject<HTMLDivElement | null>;
  readonly pageId: string;
}

interface HoveredBlock {
  readonly element: HTMLElement;
  readonly blockId: string;
  readonly url: string;
  readonly type: 'image' | 'video' | 'file';
}

/**
 * Extracts the attachment ID from a stable BFF path or presigned S3 URL.
 */
function extractAttachmentId(url: string): string | null {
  // Stable BFF path: /api/v1/pages/{pageId}/attachments/{attachmentId}/download
  const bffMatch = url.match(/\/attachments\/([^/]+)\/download/);
  if (bffMatch) return bffMatch[1];

  // Presigned S3 URL: .../pages/{pageId}/attachments/{attachmentId}/{filename}
  const s3Match = url.match(/\/attachments\/([^/]+)\//);
  if (s3Match) return s3Match[1];

  return null;
}

/**
 * Extracts the page ID from a stable BFF path.
 */
function extractPageId(url: string): string | null {
  const match = url.match(/\/pages\/([^/]+)\/attachments/);
  return match ? match[1] : null;
}

/**
 * Extracts a filename from a URL (last path segment before query params).
 */
function extractFilename(url: string): string {
  try {
    const pathname = new URL(url, 'http://placeholder').pathname;
    const segments = pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1] || '';
    // Decode and strip attachment ID patterns (UUID/)
    return decodeURIComponent(last) || 'attachment';
  } catch {
    return 'attachment';
  }
}

/**
 * Maps a block type to a MIME type hint for the modal display.
 */
function blockTypeToMime(type: 'image' | 'video' | 'file'): string | undefined {
  if (type === 'image') return 'image/unknown';
  if (type === 'video') return 'video/unknown';
  return undefined;
}

export function MediaBlockOverlay({
  editor,
  containerRef,
  pageId,
}: MediaBlockOverlayProps): React.ReactElement | null {
  const [hovered, setHovered] = useState<HoveredBlock | null>(null);
  const [overlayPos, setOverlayPos] = useState<{ top: number; right: number } | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Track mouse over media blocks
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    function handleMouseOver(e: MouseEvent): void {
      const target = e.target as HTMLElement;

      // Find closest media block wrapper
      const mediaBlock =
        target.closest('[data-content-type="image"]') ||
        target.closest('[data-content-type="video"]') ||
        target.closest('[data-content-type="file"]');

      if (mediaBlock && container) {
        const blockEl = mediaBlock as HTMLElement;
        const blockId = blockEl.getAttribute('data-id') || '';
        const img = blockEl.querySelector('img');
        const video = blockEl.querySelector('video');
        const link = blockEl.querySelector('a');

        let url = '';
        let type: 'image' | 'video' | 'file' = 'file';

        if (img) {
          url = img.src || '';
          type = 'image';
        } else if (video) {
          url = video.src || '';
          type = 'video';
        } else if (link) {
          url = link.href || '';
          type = 'file';
        }

        // Calculate overlay position relative to container
        const containerRect = container.getBoundingClientRect();
        const blockRect = blockEl.getBoundingClientRect();

        setHovered({ element: blockEl, blockId, url, type });
        setOverlayPos({
          top: blockRect.top - containerRect.top + container.scrollTop + 8,
          right: containerRect.right - blockRect.right + 8,
        });
      }
    }

    function handleMouseOut(e: MouseEvent): void {
      const relatedTarget = e.relatedTarget as HTMLElement | null;

      // Don't hide if moving to the overlay itself
      if (overlayRef.current?.contains(relatedTarget)) return;

      // Don't hide if still within a media block
      if (relatedTarget?.closest('[data-content-type="image"], [data-content-type="video"], [data-content-type="file"]')) return;

      setHovered(null);
      setOverlayPos(null);
    }

    container.addEventListener('mouseover', handleMouseOver);
    container.addEventListener('mouseout', handleMouseOut);

    return () => {
      container.removeEventListener('mouseover', handleMouseOver);
      container.removeEventListener('mouseout', handleMouseOut);
    };
  }, [containerRef]);

  const [downloadTarget, setDownloadTarget] = useState<(DownloadTarget & { url: string }) | null>(null);

  const handleDownload = useCallback(() => {
    if (!hovered?.url) return;

    const filename = extractFilename(hovered.url);
    const mimeType = blockTypeToMime(hovered.type);

    setDownloadTarget({ filename, mimeType, url: hovered.url });
  }, [hovered]);

  const handleConfirmDownload = useCallback(async () => {
    if (!downloadTarget) return;

    const attachmentId = extractAttachmentId(downloadTarget.url);
    const resolvedPageId = extractPageId(downloadTarget.url) || pageId;

    let downloadUrl = downloadTarget.url;

    if (attachmentId) {
      const result = await pagesApi.downloadAttachment(resolvedPageId, attachmentId);
      if (result.url) {
        downloadUrl = result.url;
      }
    }

    // Use anchor element to trigger download — avoids pop-up blockers
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = downloadTarget.filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [downloadTarget, pageId]);

  const handleDelete = useCallback(() => {
    if (!editor || !hovered) return;

    // Find the block by looking through the document
    const blocks = editor.document;
    const targetBlock = blocks.find(
      (b: any) => b.props?.url === hovered.url || b.id === hovered.blockId
    );

    if (targetBlock) {
      editor.removeBlocks([targetBlock]);
      editor.focus();
    }

    setHovered(null);
    setOverlayPos(null);
  }, [editor, hovered]);

  const handleClickBelow = useCallback(() => {
    if (!editor || !hovered) return;

    // Find the block and insert a paragraph after it
    const blocks = editor.document;
    const targetBlock = blocks.find(
      (b: any) => b.props?.url === hovered.url || b.id === hovered.blockId
    );

    if (targetBlock) {
      // Check if there's already a paragraph after
      const idx = blocks.indexOf(targetBlock);
      if (idx === blocks.length - 1 || blocks[idx + 1]?.type !== 'paragraph') {
        editor.insertBlocks(
          [{ type: 'paragraph', content: [] } as any],
          targetBlock,
          'after'
        );
      }
      // Move cursor to the block after
      const updatedBlocks = editor.document;
      const nextBlock = updatedBlocks[updatedBlocks.indexOf(targetBlock) + 1];
      if (nextBlock) {
        editor.setTextCursorPosition(nextBlock, 'start');
      }
    }

    editor.focus();
    setHovered(null);
    setOverlayPos(null);
  }, [editor, hovered]);

  if (!hovered || !overlayPos) return (
    <DownloadConfirmModal
      open={downloadTarget !== null}
      onClose={() => setDownloadTarget(null)}
      onConfirm={handleConfirmDownload}
      target={downloadTarget}
    />
  );

  return (
    <>
      <div
        ref={overlayRef}
        className="absolute z-20 flex items-center gap-1 bg-card/90 backdrop-blur-sm border rounded-md shadow-md px-1.5 py-1"
        style={{ top: `${overlayPos.top}px`, right: `${overlayPos.right}px` }}
        onMouseLeave={() => { setHovered(null); setOverlayPos(null); }}
      >
      <button
        type="button"
        onClick={handleDownload}
        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        title="Download"
        aria-label="Download file"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4" aria-hidden="true">
          <path d="M8 10.5l-3.5-3.5h2.5V3h2v4h2.5L8 10.5zM3 12h10v1H3v-1z" />
        </svg>
      </button>
      <button
        type="button"
        onClick={handleClickBelow}
        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        title="Insert text below"
        aria-label="Insert text below this block"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4" aria-hidden="true">
          <path d="M3 8h10v1H3V8zm3 3h4v1H6v-1zm-3 3h10v1H3v-1z" />
        </svg>
      </button>
      <button
        type="button"
        onClick={handleDelete}
        className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
        title="Remove"
        aria-label="Remove this block"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4" aria-hidden="true">
          <path d="M5 2v1H3v1h10V3h-2V2H5zm1 0h4v1H6V2zM3 5v8c0 .6.4 1 1 1h8c.6 0 1-.4 1-1V5H3zm3 7H5V7h1v5zm2 0H7V7h1v5zm2 0H9V7h1v5z" />
        </svg>
      </button>
      </div>
      <DownloadConfirmModal
        open={downloadTarget !== null}
        onClose={() => setDownloadTarget(null)}
        onConfirm={handleConfirmDownload}
        target={downloadTarget}
      />
    </>
  );
}
